import { LitElement, type TemplateResult, html, nothing } from "lit";

import { type DirectiveResult } from "lit/async-directive.js";
import { customElement, property, query, state } from "lit/decorators.js";

import { autoUpdate, computePosition, flip, offset, shift } from "@floating-ui/dom";
import "@phosphor-icons/webcomponents/PhMoon";
import "@phosphor-icons/webcomponents/PhSignIn";
import "@phosphor-icons/webcomponents/PhSignOut";
import "@phosphor-icons/webcomponents/PhSun";
import "@phosphor-icons/webcomponents/PhCrown";
import "@phosphor-icons/webcomponents/PhQuestion";
import "@phosphor-icons/webcomponents/PhGear";
import { type Language, LanguageController } from "ui-common/i18n";
import { type Theme, ThemeController } from "ui-common/theme";
import { featureFlag } from "../wow-feature/wow-feature";

import { gbFlag } from "./flag-gb.svg";
import { nlFlag } from "./flag-nl.svg";
import { styles } from "./menu-button.css";

export interface MenuItem {
  id: string;
  href?: string;
  icon: TemplateResult;
  label: DirectiveResult;
  onClick?: () => void;
}

export interface MenuButtonLabels {
  openMenu: DirectiveResult;
  viewProfile: DirectiveResult;

  selectEnglish: DirectiveResult;
  selectDutch: DirectiveResult;
  lightTheme: DirectiveResult;
  darkTheme: DirectiveResult;
  manage: DirectiveResult;
  signOut: DirectiveResult;
  signIn: DirectiveResult;
  becomeCreator?: DirectiveResult;
}

@customElement("menu-button")
export class MenuButton extends LitElement {
  @property({ type: String }) userName = "";
  @property({ type: String }) userHandle?: string;
  @property({ type: String }) userAvatar?: string;
  @property({ type: String }) userId?: string;

  @property({ type: String }) memberSinceYear = "";

  @property({ type: Array }) menuItems: MenuItem[] = [];
  @property({ type: Object }) labels!: MenuButtonLabels;
  @property({ type: Boolean }) showThemeToggle = true;

  @state()
  private _isOpen = false;

  @query("button")
  private _button!: HTMLButtonElement;

  @query("#menu-popover")
  private _popover!: HTMLElement;

  @property({ type: Boolean, reflect: true })
  public hideViewProfileWhenLoggedIn: boolean = false;

  private _cleanup?: () => void;
  private languageController = new LanguageController(this);
  private themeController = new ThemeController(this);

  static override styles = styles;

  private _setupPositioning() {
    if (!this._button || !this._popover) return;

    this._cleanup = autoUpdate(this._button, this._popover, () => {
      computePosition(this._button, this._popover, {
        placement: "bottom-end",
        middleware: [offset(8), flip(), shift({ padding: 8 })],
      }).then(({ x, y }) => {
        Object.assign(this._popover.style, {
          left: `${x}px`,
          top: `${y}px`,
        });
      });
    });
  }

  private _handleViewProfileClick(event: Event) {
    event.preventDefault();
    // Close the menu
    this._popover.hidePopover();

    // Emit view-profile event
    this.dispatchEvent(
      new CustomEvent("view-profile", {
        bubbles: true,
        composed: true,
      }),
    );
  }

  private _handleLanguageToggle(language: Language) {
    this.languageController.setLanguage(language);
  }

  private _handleThemeToggle(theme: Theme) {
    this.themeController.setTheme(theme);
  }

  private _handleMenuItemClick(item: MenuItem) {
    // Close the menu
    this._popover.hidePopover();

    // Call the item's onClick if it exists
    if (item.onClick) {
      item.onClick();
    }

    // Emit a custom event for navigation handling by parent
    if (item.href) {
      this.dispatchEvent(
        new CustomEvent("menu-navigate", {
          detail: { href: item.href, item },
          bubbles: true,
          composed: true,
        }),
      );
    }
  }

  private _handleSignOut() {
    // Close the menu
    this._popover.hidePopover();

    // Emit sign-out event
    this.dispatchEvent(
      new CustomEvent("sign-out", {
        bubbles: true,
        composed: true,
      }),
    );
  }

  private _renderMenuItems() {
    const isLoggedOut = !this.userName && !this.userHandle && !this?.userAvatar && !this.userId;

    const isFan = !this.userHandle && !!this.userId;

    const isCreator = !!this.userHandle;

    return html`
      ${featureFlag(
        isLoggedOut,
        html`
          <a class="menu-item" href="/my/sign-up">
            <app-logo class="menu-item-icon"></app-logo>
            <wow-button variant="subtle" class="menu-item-text">Join WOW</wow-button>
          </a>
          <a
            class="menu-item"
            @click=${() => this._handleMenuItemClick({ icon: html``, label: "signin", href: "/my", id: "signin" })}
          >
            <span class="menu-item-icon">
              <ph-sign-in weight="bold" size="24"></ph-sign-in>
            </span>
            <wow-button variant="subtle" class="menu-item-text">${this.labels.signIn} </wow-button>
          </a>
        `,
      )}
      ${featureFlag(
        isFan,
        html`
          ${this.menuItems.map(
            (item) => html`
              <div class="menu-item" @click=${() => this._handleMenuItemClick(item)}>
                <span class="menu-item-icon">${item.icon}</span>
                <wow-button variant="subtle" class="menu-item-text">${item.label}</wow-button>
              </div>
            `,
          )}
          <a class="menu-item" @click=${this._handleSignOut}>
            <span class="menu-item-icon">
              <ph-sign-out weight="bold" size="24"></ph-sign-out>
            </span>
            <wow-button variant="subtle" class="menu-item-text">${this.labels.signOut}</wow-button>
          </a>
        `,
      )}
      ${featureFlag(
        isCreator,
        html`
          ${this.menuItems.map(
            (item) => html`
              <div class="menu-item" @click=${() => this._handleMenuItemClick(item)}>
                <span class="menu-item-icon">${item.icon}</span>
                <wow-button variant="subtle" class="menu-item-text">${item.label}</wow-button>
              </div>
            `,
          )}
          <a class="menu-item" @click=${this._handleSignOut}>
            <span class="menu-item-icon">
              <ph-sign-out weight="bold" size="24"></ph-sign-out>
            </span>
            <wow-button variant="subtle" class="menu-item-text">${this.labels.signOut}</wow-button>
          </a>
        `,
      )}
    `;
  }

  private renderThemeToggle() {
    if (!this.showThemeToggle) return nothing;

    return html`<div class="theme-toggles">
      <button
        class="theme-toggle ${this.themeController.resolvedTheme === "light" ? "active" : ""}"
        title=${this.labels.lightTheme}
        @click=${() => this._handleThemeToggle("light")}
      >
        <ph-sun weight="bold" size="14px"></ph-sun>
      </button>
      <button
        class="theme-toggle ${this.themeController.resolvedTheme === "dark" ? "active" : ""}"
        title=${this.labels.darkTheme}
        @click=${() => this._handleThemeToggle("dark")}
      >
        <ph-moon weight="bold" size="14px"></ph-moon>
      </button>
    </div>`;
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this._cleanup?.();
  }

  private handlePopoverToggle(e: ToggleEvent) {
    this._isOpen = e.newState === "open";
    if (this._isOpen) this._setupPositioning();
  }

  override render() {
    return html`
      <button
        aria-expanded=${this._isOpen}
        aria-haspopup="true"
        aria-label=${this.labels.openMenu}
        popovertarget="menu-popover"
      >
        <div class="hamburger-container">
          <div class="hamburger-line"></div>
          <div class="hamburger-line"></div>
          <div class="hamburger-line"></div>
        </div>
      </button>

      <div id="menu-popover" popover="" @toggle=${this.handlePopoverToggle}>
        <div class="menu-content">
          <!-- User Profile Section -->
          ${this.userHandle && this.userId
            ? html`
                <section class="user-profile-section">
                  <slot name="avatar"></slot>

                  <div class="user-name">${this.userName || this.userHandle}</div>
                  <div class="member-info">${this.memberSinceYear}</div>

                  ${this.hideViewProfileWhenLoggedIn
                    ? nothing
                    : html`
                        <wow-button variant="primary" size="medium" @click=${this._handleViewProfileClick}>
                          ${this.labels.viewProfile}
                        </wow-button>
                      `}
                </section>
              `
            : nothing}

          <!-- Menu Section -->
          <section class="menu-section">
            <h3 class="menu-title">
              ${this.userName || this.userHandle || this.userId ? this.labels.manage : nothing}
            </h3>
            <nav class="menu-items">${this._renderMenuItems()}</nav>
          </section>

          <!-- Language Picker Section -->
          <section class="language-picker-section">
            <div class="language-picker-buttons">
              <div class="language-buttons">
                <button
                  class="language-button ${this.languageController.language === "en" ? "active" : ""}"
                  @click=${() => this._handleLanguageToggle("en")}
                  aria-label=${this.labels.selectEnglish}
                >
                  ${gbFlag}
                </button>
                <button
                  class="language-button ${this.languageController.language === "nl" ? "active" : ""}"
                  @click=${() => this._handleLanguageToggle("nl")}
                  aria-label=${this.labels.selectDutch}
                >
                  ${nlFlag}
                </button>
              </div>
              ${this.renderThemeToggle()}
            </div>
          </section>
        </div>
      </div>
    `;
  }
}
